import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { getActiveBrandId } from '../lib/active-brand.js';
import { brandsQuery, meQuery } from '../lib/queries.js';

// Flat `/brand` redirector — drops the founder onto their active
// brand's /home with the workbench drawer pre-opened on the overview
// tab. The standalone /brand top-nav tab was retired alongside the
// brand-hub UI (see the brand workbench design doc (CommitMind)); this redirect
// stays so existing bookmarks + the post-onboarding handoff still
// resolve to a sensible landing.
//
// File-routing gotcha: because every `brand.$slug.*.tsx` route shares
// this file's `brand.` prefix, this route ends up as their parent in
// the generated tree — meaning its `beforeLoad` fires for every
// navigation to `/brand/$slug/anything`, not just `/brand`. We guard
// the redirect on `location.pathname === '/brand'` so the parent pass-
// through is a no-op; the redirect only fires when the founder hits
// `/brand` directly. Without the guard, every brand-scoped navigation
// infinite-loops back to the redirect target.

export const Route = createFileRoute('/brand')({
  beforeLoad: async ({ location, context: { queryClient } }) => {
    if (location.pathname !== '/brand') return;
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const live = brands.filter((b) => b.archivedAt === null);
    const activeId = getActiveBrandId();
    const target = live.find((b) => b.id === activeId) ?? live[0];
    if (!target) throw redirect({ to: '/brands' });
    throw redirect({
      to: '/brand/$slug/home',
      params: { slug: target.slug },
      search: { brand: 'full', brandTab: 'overview' },
    });
  },
  // Outlet because the generated route tree makes this route the
  // parent of every `brand.$slug.*` file. When the URL is `/brand`
  // exactly, the beforeLoad redirect fires before this renders;
  // when matched as a parent, the child needs somewhere to land.
  component: () => <Outlet />,
});
